import React, { useEffect, useState } from 'react';
import { useI18n } from '../../../../../hooks/useI18n';
import { useNotificationStore } from '../../../../../hooks/useNotificationStore/notificationStore';
import { TimePeriodType } from '../../../../../types';
import { ApiError, useApiRequests } from '../../../../../hooks/useApi';
import {
  customerProductStatisticsReportWidgetApi,
  CreateUpdateCustomerProductStatisticsReportWidgetRequest,
} from '../../../../../api/widgets/customers/customerProductStatisticsReportWidgetApi';
import { messages } from '../../../../../messages';
import { useFormik } from 'formik';
import { Input, Modal } from 'antd';
import { ApiErrorDetails } from '../../../../apiErrorDetails/apiErrorDetails';
import { Form } from '../../../../form/form';
import style from '../styling/widgetRowStyling.module.scss';
import { ArticleCategoryPicker } from '../../../../pickers/articleCategoryPicker';
import { categoryCodesApi } from '../../../../../api/categoryCodesApi';
import { TimePeriodPicker } from '../../../../pickers/timePeriodPicker';
import { TwitterPicker } from 'react-color';
import { defaultWidgetColors } from '../../../../internal/widgets/widgetColors';
import { CustomerWidgetCategorySelect } from '../categories/customerWidgetCategorySelect';

export const UpdateCustomerProductStatisticsReportWidgetModal = ({
  userWidgetId,
  onCancel,
  onSubmit,
}: {
  userWidgetId: number;
  onCancel: () => void;
  onSubmit: () => void;
}) => {
  const { t } = useI18n();
  const { notify } = useNotificationStore();

  const updateWidget = useApiRequests(
    customerProductStatisticsReportWidgetApi.updateWidget,
  );

  const widget = useApiRequests(
    customerProductStatisticsReportWidgetApi.getWidget,
  );

  const [name, setName] = useState<string>('');
  const [widgetCategoryId, setWidgetCategoryId] = useState<number | undefined>(
    undefined,
  );
  const [color, setColor] = useState<string | undefined>(undefined);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [timePeriods, setTimePeriods] = useState<{
    type: TimePeriodType;
    periods: string[];
  }>({
    type: TimePeriodType.Yearly,
    periods: [],
  });

  const [error, setError] = useState<ApiError>();

  useEffect(() => {
    widget
      .create({
        onSuccess: (response) => {
          setName(response.name);
          setWidgetCategoryId(response.widgetCategoryId);
          setColor(response.color);
          setCategoryIds(response.categories.map((category) => category.id));
          setTimePeriods({
            type: response.timePeriodType,
            periods: response.timePeriods,
          });
        },
        onError: setError,
      })
      .send(userWidgetId);
  }, []);

  const handleFormSubmit = () => {
    const updateWidgetData: CreateUpdateCustomerProductStatisticsReportWidgetRequest =
      {
        name: name,
        widgetCategoryId: widgetCategoryId,
        color: color,
        categoryIds: categoryIds,
        timePeriods: timePeriods.periods,
        timePeriodType: timePeriods.type,
      };

    updateWidget
      .create({
        onSuccess: () => {
          notify({
            title: t(messages.widgets.widgetCreated),
            type: 'success',
          });
          onSubmit();
        },
        onError: setError,
      })
      .send(userWidgetId, updateWidgetData);
  };

  const formik = useFormik<FormData>({
    initialValues: {
      name: '',
      categoryIds: [],
    },
    onSubmit: handleFormSubmit,
  });

  function handleWidgetCategoryOnChange(widgetCategoryId: string | undefined) {
    const id =
      widgetCategoryId === undefined
        ? widgetCategoryId
        : parseInt(widgetCategoryId);
    setWidgetCategoryId(id);
  }

  return (
    <Modal
      title={t(messages.widgets.productStatistics.update)}
      visible={true}
      closable={false}
      onCancel={onCancel}
      onOk={formik.submitForm}
      okText={t(messages.shared.save)}
      width={720}
    >
      {error && <ApiErrorDetails error={error} />}
      <Form form={formik}>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.name)}</h4>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(messages.widgets.widgetNamePlaceholder)}
          />
          <CustomerWidgetCategorySelect
            value={widgetCategoryId?.toString()}
            onChange={handleWidgetCategoryOnChange}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.color)}</h4>
          <TwitterPicker
            width="100%"
            triangle="hide"
            colors={defaultWidgetColors}
            color={color}
            onChange={(color) => setColor(color.hex)}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.reports.articleCategory)}</h4>
          <ArticleCategoryPicker
            categoryIds={categoryIds}
            onChange={setCategoryIds}
            categoryRequestFactory={(_) => categoryCodesApi.getCategories}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.reports.timePeriod)}</h4>
          <TimePeriodPicker
            selectedTimePeriods={timePeriods.periods}
            selectedTimePeriodsType={timePeriods.type}
            onChange={setTimePeriods}
          />
        </div>
      </Form>
    </Modal>
  );
};

interface FormData {
  name: string;
  categoryIds: number[];
}
